import { useCallback, useMemo, useRef, useState } from 'react';
import type { HubLanguageState } from './cbcTranslation';

/**
 * Language detection + auto-translation support for form-engine-portable
 * document steps (criminal background, academic record, diploma, …).
 *
 * OWNERSHIP MOVED HUB-SIDE (Hub PR #1797): the Hub's server pipeline detects
 * the language and produces the English translation itself, triggered by the
 * step submit. The portal no longer calls any analyze/translate endpoint —
 * this hook just projects the polled pipeline state (see lib/cbcTranslation)
 * into the AP context shape the render-only engine reads
 * (FormEngineAPProvider): per-field analysis results for the badge, and
 * `translationDataFromDB` for the completed-translation notice + download
 * link. `translateMutation` is intentionally inert: the engine auto-fires it
 * for non-English results, but translation is no longer a client action.
 */

/** Document fields the engine pairs with a translation field and expects
 * language detection for (mirrors the engine's internal pairing table). */
export const LANGUAGE_DETECTION_FIELDS = new Set([
  'criminal_background_clearance',
  'academic_record_document',
  'state_certification_upload',
  'certificate_employment_document',
  'foreign_credential_evaluation_document',
  'foreign_credential_document',
  'cultural_exchange_document',
  'cultural_exchange_agreement_document',
  'degree_diploma_document',
]);

export interface LanguageAnalysisResult {
  isEnglish: boolean;
  detectedLanguage?: string;
  confidence?: string | number;
  requiresTranslation?: boolean;
  // Index signature retained because the engine passes these properties
  // through its AP context without a fixed schema.
  [k: string]: unknown;
}

/**
 * Host-side AP-context builder. `hub` carries the Hub-pipeline-derived state
 * for the current step (results/status per field + the active translation
 * object); local state only backs the engine's manual-override plumbing.
 */
export function useLanguageSupport(stepKey: string, hub?: HubLanguageState) {
  const [manualLanguageOverride, setManualLanguageOverride] = useState<Record<string, unknown>>({});
  const [localStatusByField, setLocalStatusByField] = useState<Record<string, string>>({});
  const [documentVersions, setDocumentVersions] = useState<Record<string, number>>({});
  const abortControllersRef = useRef<Record<string, AbortController>>({});
  // stepKey retained in the signature for call-site clarity/telemetry parity.
  void stepKey;

  const results = useMemo(
    () => ({ ...(hub?.results ?? {}) }) as Record<string, LanguageAnalysisResult>,
    [hub?.results],
  );
  const statusByField = useMemo(
    () => ({ ...localStatusByField, ...(hub?.statusByField ?? {}) }),
    [localStatusByField, hub?.statusByField],
  );
  const analysisStatus = hub?.analysisStatus ?? {};
  const translation = hub?.translation ?? null;
  const extractingByField: Record<string, boolean> = useMemo(() => ({}), []);

  const buildContext = useCallback(
    (defaultContext: Record<string, unknown>) => {
      const hook = {
        languageAnalysisResults: results,
        languageStatusByField: statusByField,
        analysisStatus,
        extractingByField,
        manualLanguageOverride,
        documentVersions,
        // Detection is server-side now; the engine's re-analyze affordance is
        // a no-op (the pipeline re-runs automatically on re-submit).
        analyzeDocument: async () => {},
        cancelAnalysis: (fieldName: string) => {
          abortControllersRef.current[fieldName]?.abort?.();
        },
        clearAnalysis: (fieldName: string) => {
          setLocalStatusByField(({ [fieldName]: _, ...rest }) => rest);
        },
        setManualOverride: (fieldName: string, value: unknown) =>
          setManualLanguageOverride((p) => ({ ...p, [fieldName]: value })),
      };
      const adapter = {
        hook,
        setLanguageAnalysisResults: () => {},
        setLanguageStatusByField: setLocalStatusByField,
        setExtractingByField: () => {},
        extractingByField,
        documentVersions,
        setDocumentVersions,
        analysisStatus,
        setAnalysisStatus: () => {},
        manualLanguageOverride,
        setManualLanguageOverride,
        abortControllersRef,
        analyzedDocuments: new Set<string>(),
        setAnalyzedDocuments: () => {},
      };
      return {
        ...defaultContext,
        languageAnalysis: { adapter, results, statusByField, extractingByField },
        // Inert by design: translation happens in the Hub pipeline. The engine
        // auto-fires mutate() for non-English results; swallowing it keeps the
        // UI on the pipeline-driven translationDataFromDB state.
        translateMutation: {
          isPending: translation != null && (translation as { status?: string }).status === 'in_progress',
          mutate: () => {},
          mutateAsync: async () => ({}),
        },
        translationDataFromDB: translation,
      };
    },
    [results, statusByField, analysisStatus, extractingByField, manualLanguageOverride, documentVersions, translation],
  );

  return useMemo(() => ({ buildContext }), [buildContext]);
}
